import { DOCUMENT } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, catchError, defer, filter, finalize, shareReplay, of, map, tap, throwError } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { AuthSession, Token, TokenResponse, RefreshToken } from "../../../models/api/auth";
import { User } from "../../../models/api/user";
import { ResponseSuccess } from "../../../models/shared/responseSuccess";
import { CacheService } from "../../cache/cache.service";
import { PermissionService } from "../permission/permission.service";
import { SystemAlertService } from "../system-alert/sys-alert.service";
import { WikiAccessLevel } from "../../../enums/wikiAccessLevels";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private cache = inject(CacheService);
  private tokenKey = "token";
  private refreshTokenKey = "refresh_token";
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private role!: string;
  private permissionsService = inject(PermissionService);
  private sysAlertService = inject(SystemAlertService);
  private user: Observable<User> | undefined;
  private userCacheGeneration = 0;
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;

  private refreshInFlight$?: Observable<void>;
  private sessionGeneration = 0;

  constructor() {
    this.setPermissions(this.localStorage?.getItem(this.tokenKey) || null);
    this.setRole(this.localStorage?.getItem(this.tokenKey) || null);
  }

  /** Retrieves and caches the authenticated user's complete profile. */
  public getAuthenticatedUser(): Observable<User> {
    if (!this.user) {
      const session = this.getSession();
      const requestGeneration = ++this.userCacheGeneration;
      const user = this.http.get<User>(environment.baseUrl + "/me").pipe(
        this.apiErrorHandler.handle("user"),
        filter(() => {
          if (session && !this.isCurrentSession(session)) {
            if (this.userCacheGeneration === requestGeneration) {
              this.user = undefined;
            }
            return false;
          }
          return true;
        }),
        catchError((errorMessage: string) => {
          if (this.userCacheGeneration === requestGeneration) {
            this.user = undefined;
            this.logout();
            this.router.navigate(["/login"]);
          }
          return throwError(() => errorMessage);
        }),
        shareReplay(1)
      );
      this.user = user;
    }
    return this.user;
  }

  /** Updates only the authenticated user's persisted locale preference. */
  public updatePreferredLocale(preferredLocale: "de" | "en"): Observable<User> {
    const session = this.getSession();

    return this.http.patch<User>(environment.baseUrl + "/me", { preferredLocale }).pipe(
      tap((profile) => {
        if (!session || this.isCurrentSession(session)) {
          this.userCacheGeneration++;
          this.user = of(profile);
        }
      })
    );
  }

  /**
   * Checks if the user is authenticated.
   * @returns Observable indicating authentication status.
   */
  public isAuth$(): Observable<boolean> {
    return this.checkIfAuthenticated();
  }

  /**
   * Refreshes the authenticated session without performing UI side effects.
   * Concurrent callers share the same request until it settles.
   */
  public refreshSession(): Observable<void> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = defer(() => {
        const session = this.getSession();
        const refreshToken = this.localStorage?.getItem(this.refreshTokenKey);
        if (!session || !refreshToken || !this.hasValidRefreshToken(refreshToken)) {
          return throwError(() => new Error("A valid refresh token is required."));
        }

        return this.http.post<TokenResponse>(environment.baseUrl + "/auth/token", { refreshToken }).pipe(
          this.apiErrorHandler.handle("user"),
          map((res) => {
            if (!res.token || !res.refreshToken || !this.hasValidAccessToken(res.token)) {
              throw new Error("The token refresh response is invalid.");
            }

            if (this.isCurrentSession(session) && this.localStorage?.getItem(this.refreshTokenKey) === refreshToken) {
              const refreshedToken = this.decodeToken<Token>(res.token);
              if (!refreshedToken || refreshedToken.id !== session.userId) {
                throw new Error("The token refresh response belongs to a different user.");
              }

              this.storeSession(res.token, res.refreshToken, false);
            }
          })
        );
      }).pipe(
        finalize(() => {
          this.refreshInFlight$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.refreshInFlight$;
  }

  /**
   * Checks whether an access token exists and has not expired.
   */
  public hasValidAccessToken(token = this.getToken()): boolean {
    const decodedToken = token ? this.decodeToken<Token>(token) : null;
    return !!decodedToken && this.checkIfPermissionArrayExists(decodedToken) && decodedToken.exp * 1000 > Date.now();
  }

  /**
   * Checks if the permission array exists in the token.
   * @param decodedToken The decoded token.
   * @returns True if permissions exist, otherwise false.
   */
  private checkIfPermissionArrayExists(decodedToken: Token): boolean {
    return !!decodedToken.permissions;
  }

  /**
   * Checks if the user is authenticated.
   * @returns Observable indicating authentication status.
   */
  private checkIfAuthenticated(): Observable<boolean> {
    const token = this.localStorage?.getItem(this.tokenKey);

    if (!token) {
      return of(false);
    }

    if (this.hasValidAccessToken(token)) {
      this.setPermissions(token);
      return of(true);
    }

    return this.refreshSession().pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Gets the wiki access level stored in the local storage.
   * @returns The wiki access level stored in the local storage, or null if no token is found.
   */
  public getWikiAccessLevel(): WikiAccessLevel | null {
    const token = this.localStorage?.getItem(this.tokenKey);
    if (token) {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      return decodedToken.faultListPermission;
    } else {
      return null;
    }
  }

  /**
   * Initiates password recovery.
   * @param email The email address.
   * @returns Observable with success status.
   */
  public forgotPassword(email: string): Observable<ResponseSuccess> {
    return this.http
      .post<{ message: string; userId: string }>(environment.baseUrl + "/auth/forgotpassword", {
        email,
      })
      .pipe(
        this.apiErrorHandler.handle("user"),
        map((res) => {
          if (res.message) {
            if (res.userId) {
              this.localStorage?.setItem("userId", res.userId);
            }
            return { success: true };
          } else {
            return { success: false };
          }
        })
      );
  }

  private hasValidRefreshToken(token: string): boolean {
    const decodedToken = this.decodeToken<RefreshToken>(token);
    return !!decodedToken && decodedToken.exp * 1000 > Date.now();
  }

  private decodeToken<T>(token: string): T | null {
    try {
      const payload = token.split(".")[1];
      return payload ? (JSON.parse(atob(payload)) as T) : null;
    } catch {
      return null;
    }
  }

  /**
   * Logs in the user.
   * @param username The username.
   * @param password The password.
   * @returns Observable with login status.
   */
  public login(username: string, password: string) {
    return this.http
      .post<TokenResponse>(environment.baseUrl + "/auth/login", {
        username,
        password,
      })
      .pipe(
        this.apiErrorHandler.handle("user"),
        tap((res: TokenResponse) => {
          if (res.token && res.refreshToken) {
            this.storeSession(res.token, res.refreshToken);
          }
        })
      );
  }

  /**
   * Logs out the user.
   */
  public logout() {
    this.sessionGeneration++;
    this.clearSessionStorage();
    this.localStorage?.removeItem(this.tokenKey);
    this.localStorage?.removeItem(this.refreshTokenKey);
    this.localStorage?.removeItem("userId");
    this.invalidateUserCache();
    this.cache.clear();
    this.sysAlertService.resetReadStatus();
    this.role = "";
  }

  private clearSessionStorage(): void {
    try {
      this.document.defaultView?.sessionStorage.clear();
    } catch {
      // Logout must still complete when browser storage is unavailable.
    }
  }

  /**
   * Retrieves the current token.
   * @returns The current token or null.
   */
  public getToken(): string | null {
    return this.localStorage?.getItem(this.tokenKey) || null;
  }

  /** Captures the current authenticated identity for callers that must reject stale async work. */
  public getSession(): AuthSession | null {
    const accessToken = this.getToken();
    const token = accessToken ? this.decodeToken<Token>(accessToken) : null;
    return accessToken && token?.id ? { generation: this.sessionGeneration, userId: token.id } : null;
  }

  /** Returns whether a captured session still matches the active logical session and identity. */
  public isCurrentSession(session: AuthSession): boolean {
    const current = this.getSession();
    return !!current && current.generation === session.generation && current.userId === session.userId;
  }

  /**
   * Sets the user role from the JWT token.
   * @param jwtToken The JWT token.
   */
  private setRole(jwtToken: string | null) {
    if (jwtToken) {
      const decoded = JSON.parse(atob(jwtToken.split(".")[1]));
      const role = decoded.user_type;
      if (role) {
        this.role = role;
      }
    }
  }

  /**
   * Sets user permissions from the JWT token.
   * @param jwtToken The JWT token.
   */
  private setPermissions(jwtToken: string | null) {
    if (jwtToken) {
      const decoded = JSON.parse(atob(jwtToken.split(".")[1]));
      const permissions = decoded.permissions;
      if (permissions) {
        this.permissionsService.setUserPermissions(permissions);
      }
    }
  }

  private storeSession(token: string, refreshToken: string, replaceUser = true): void {
    if (replaceUser) {
      this.sessionGeneration++;
      this.invalidateUserCache();
    }
    this.localStorage?.setItem(this.tokenKey, token);
    this.localStorage?.setItem(this.refreshTokenKey, refreshToken);
    this.setRole(token);
    this.setPermissions(token);
  }

  private invalidateUserCache(): void {
    this.userCacheGeneration++;
    this.user = undefined;
  }

  /**
   * Retrieves the user role.
   * @returns The user role.
   */
  public getRole(): string {
    return this.role;
  }

  /**
   * Sends an activation code to the user's email address.
   * @param email The user's email address.
   * @returns Observable with the response status.
   */
  public userActivation(email: string): Observable<string | null> {
    return this.http.post<TokenResponse>(environment.baseUrl + "/auth/activate", { email }).pipe(
      this.apiErrorHandler.handle("user"),
      map((res: TokenResponse) => {
        if (res.message && res.message === "Activate code sent to email." && res.userId) {
          this.localStorage?.setItem("userId", res.userId);
          return res.userId;
        } else {
          return null;
        }
      })
    );
  }

  /**
   * Confirms the OTP code entered by the user.
   * @param otp The OTP code.
   * @returns Observable with the response status.
   */
  public confirmOtpActivation(otp: string): Observable<string> {
    const userId = this.localStorage?.getItem("userId");
    return this.http
      .post<TokenResponse>(environment.baseUrl + "/auth/otp/verify", {
        code: otp,
        userId: userId,
      })
      .pipe(
        this.apiErrorHandler.handle("user"),
        map((res: TokenResponse) => {
          if (res.token) {
            this.localStorage?.setItem("activationToken", res.token);
            return res.token;
          } else {
            throw new Error("Token is missing.");
          }
        })
      );
  }

  /**
   * Calls the set password endpoint with the password and activation token.
   * @param password The new password.
   * @returns Observable with the response status.
   */
  public setPassword(password: string): Observable<ResponseSuccess> {
    return this.http
      .post(environment.baseUrl + "/auth/setpassword", {
        password,
        token: this.localStorage?.getItem("activationToken"),
      })
      .pipe(
        this.apiErrorHandler.handle("user"),
        map((res: TokenResponse) => {
          this.localStorage?.removeItem("activationToken");
          if (res.message) {
            if (res.message === "Password set successfully.") {
              return { success: true };
            } else {
              return { success: false, errorMessage: res.message };
            }
          } else {
            return { success: false };
          }
        })
      );
  }
}
